import React from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { IEcommerceQuestionViewProps } from '../../types.ts'
import style from './Header.module.scss'

// Icons
import help24x7Icon from '../assets/icons/help-24-7.svg'
import signInIcon from '../assets/icons/sign-in.svg'
import cartIcon from '../assets/icons/cart.svg'
import usFlag from '../assets/icons/us-flag.svg'

interface IHeaderProps {
  schema: IEcommerceQuestionViewProps['schema']
  total: number
  findability: boolean
  onHomeClick: () => void
  onCartOpen: () => void
}

const SUB_NAV_ITEMS = [
  { key: 'navDog', dropdown: true },
  { key: 'navCat', dropdown: true },
  { key: 'navOtherAnimals', dropdown: true },
  { key: 'navPharmacy', dropdown: true },
  { key: 'navServices', dropdown: true },
  { key: 'navLearn', dropdown: false },
  { key: 'navGiveBack', dropdown: true },
  { key: 'navTodaysDeals', dropdown: false }
]

const Header = ({ schema, total, findability, onHomeClick, onCartOpen }: IHeaderProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()

  // Mobile app top bar: back, full-width search pill, chat, cart — mirrors the
  // Chewy app layout (Costco Header pattern). Brand-blue bar kept like desktop.
  if (viewport.mobile)
    return (
      <header className={style.headerMobile} data-testid="SW-EC-Header">
        <button
          className={style.backBtn}
          type="button"
          aria-label={t('questions', 'ec', 'chewy', 'back')}
          onClick={onHomeClick}
        >
          <i className="pi pi-chevron-left" />
        </button>

        <div className={style.searchMobile} onClick={onHomeClick}>
          <i className={`pi pi-search ${style.searchMobileIcon}`} />
          <div
            className={style.searchMobileText}
            dangerouslySetInnerHTML={{
              __html: schema.search_condition || t('questions', 'ec', 'chewy', 'searchPlaceholderMobile')
            }}
          />
          <i className={`pi pi-qrcode ${style.searchMobileScan}`} />
        </div>

        <EmptyLink color="#1C49C4">
          <i className={`pi pi-comment ${style.headerMobileIcon}`} />
        </EmptyLink>

        {findability ? (
          <EmptyLink color="#1C49C4">
            <div className={style.cartMobile}>
              <span className={style.cartMobileIcon} />
              {total > 0 && <div className={style.cartBadge}>{total > 99 ? '99+' : total}</div>}
            </div>
          </EmptyLink>
        ) : (
          <button
            className={style.cartMobile}
            type="button"
            aria-label={t('questions', 'ec', 'chewy', 'cart')}
            onClick={onCartOpen}
          >
            <span className={style.cartMobileIcon} />
            {total > 0 && <div className={style.cartBadge}>{total > 99 ? '99+' : total}</div>}
          </button>
        )}
      </header>
    )

  return (
    <header className={style.header} data-testid="SW-EC-Header">
      <div className={style.topRow}>
        <div className={style.logo} onClick={onHomeClick} role="img" aria-label="Chewy" />

        <div className={style.search}>
          <div
            className={style.searchText}
            onClick={onHomeClick}
            dangerouslySetInnerHTML={{
              __html: schema.search_condition || t('questions', 'ec', 'chewy', 'searchPlaceholder')
            }}
          />
          <div className={style.searchIcons}>
            <EmptyLink color="#04060b">
              <i className={`pi pi-times ${style.searchClear}`} />
            </EmptyLink>
            <EmptyLink color="#1C49C4">
              <i className={`pi pi-search ${style.searchIcon}`} />
            </EmptyLink>
          </div>
        </div>

        {/* US-only store: a static region indicator (local flag asset), not a country selector. */}
        <EmptyLink color="#1C49C4">
          <div className={`${style.iconLink} ${style.country}`}>
            <img src={usFlag} alt="" role="presentation" className={style.flag} />
            <span>USA</span>
            <i className="pi pi-chevron-down" />
          </div>
        </EmptyLink>

        <EmptyLink color="#1C49C4">
          <div className={style.useApp}>{t('questions', 'ec', 'chewy', 'useApp')}</div>
        </EmptyLink>

        <EmptyLink color="#1C49C4">
          <div className={style.iconLink}>
            <img src={help24x7Icon} alt="" role="presentation" className={style.iconLinkGlyph} />
            <span>{t('questions', 'ec', 'chewy', 'help247')}</span>
            <i className="pi pi-chevron-down" />
          </div>
        </EmptyLink>

        <EmptyLink color="#1C49C4">
          <div className={style.iconLink}>
            <img src={signInIcon} alt="" role="presentation" className={style.iconLinkGlyph} />
            <span>{t('questions', 'ec', 'chewy', 'signIn')}</span>
            <i className="pi pi-chevron-down" />
          </div>
        </EmptyLink>

        {findability ? (
          <EmptyLink color="#1C49C4">
            <div className={style.cart}>
              <img src={cartIcon} alt="" role="presentation" className={style.cartIcon} />
              {total > 0 && <div className={style.cartBadge}>{total > 99 ? '99+' : total}</div>}
              <span>{t('questions', 'ec', 'chewy', 'cart')}</span>
              <i className="pi pi-chevron-down" />
            </div>
          </EmptyLink>
        ) : (
          <button className={style.cart} onClick={onCartOpen} type="button">
            <img src={cartIcon} alt="" role="presentation" className={style.cartIcon} />
            {total > 0 && <div className={style.cartBadge}>{total > 99 ? '99+' : total}</div>}
            <span>{t('questions', 'ec', 'chewy', 'cart')}</span>
            <i className="pi pi-chevron-down" />
          </button>
        )}
      </div>

      <div className={style.subNav}>
        {SUB_NAV_ITEMS.map(item => (
          <EmptyLink key={item.key} color="#1C49C4">
            <div className={style.subNavItem}>
              <span>{t('questions', 'ec', 'chewy', item.key)}</span>
              {item.dropdown && <i className="pi pi-chevron-down" />}
            </div>
          </EmptyLink>
        ))}
        <div className={style.freeDelivery}>{t('questions', 'ec', 'chewy', 'freeDeliveryNote')}</div>
      </div>
    </header>
  )
}

export default Header
